/* rv-home-hero (DESIGN-SYSTEM.md §9.1.1): flavor chips, the switch pour + jar crossfade, the deferred
   second jar, load-pour cleanup, the sticker kept clear of the words on desktop (fitSeal), and #buy links that focus
   the buy box tiles. Page colors: rv-flavor-state.js. The sticker's motion and pause are CSS only (rv-home-hero.css). */
(() => {
  const D = document;

  if (!customElements.get('rv-home-hero')) {
    class RvHomeHero extends HTMLElement {
      connectedCallback() {
        this.fills = [...this.querySelectorAll('.rv-home-hero__fill')];
        this.jars = [...this.querySelectorAll('.rv-home-hero__jar-img')];
        /* "A photo in a box" mode: one layer per look (main, plus a flavor's own photo), swapped with a blurred crossfade */
        this.photos = [...this.querySelectorAll('.rv-home-hero__photo-layer')];
        this.chips = [...this.querySelectorAll('.rv-home-hero__chip input')];
        this.label = this.querySelector('[data-rv-cta-label]');
        /* "Homepage opens on the Duo" (Cris, 2026-10-03): the server renders the Duo picked (chip checked, Duo
           button label), so the chips and the button follow every change from the start, restores included. */
        this.startDuo = this.dataset.start === 'duo';
        this.touched = this.startDuo;

        /* §8.5: otherwise neither chip is preselected. Clear any state the browser restored (reload/back), and
           treat flavor events fired while the page is still starting up (the buy box restoring ?flavor= or the
           session pick) as restores: instant swap, chips untouched. */
        if (!this.startDuo) this.chips.forEach((c) => (c.checked = false));
        this.ready = false;
        const go = () => setTimeout(() => (this.ready = true));
        if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', go, { once: true });
        else go();

        this.onFlavor = (e) => this.show(e.detail, !this.ready && !this.own);
        D.addEventListener('rv:flavor-change', this.onFlavor);

        this.chips.forEach((c) =>
          c.addEventListener('change', () => {
            if (!c.checked) return;
            this.touched = true;
            this.pick(c);
          })
        );

        /* the load pour ends when the jar lands */
        this.addEventListener('animationend', (e) => {
          if (e.target.classList.contains('rv-home-hero__jar')) this.classList.remove('is-pour');
        });

        /* the other jar loads after window load, never competing with the LCP */
        const later = () => setTimeout(() => [...this.jars, ...this.photos].forEach((j) => this.hydrate(j)), 300);
        if (D.readyState === 'complete') later();
        else window.addEventListener('load', later, { once: true });

        /* follow a flavor restored this session (rv-flavor-state sets it on <html>) */
        const cur = D.documentElement.getAttribute('data-rv-flavor');
        if (cur) this.show({ handle: cur }, true);

        /* desktop: keep the sticker clear of every word, at every screen size (see fitSeal) */
        this.seal = this.querySelector('.rv-home-hero__seal');
        this.desk = matchMedia('(min-width: 990px)');
        if (this.seal && 'ResizeObserver' in window) {
          this.refit = () => this.fitSeal();
          this.ro = new ResizeObserver(this.refit); /* at most once a frame, and on first observe */
          this.ro.observe(this);
          if (D.fonts && D.fonts.ready) D.fonts.ready.then(this.refit);
          window.addEventListener('load', this.refit, { once: true });
          this.addEventListener('animationend', this.refit);
        }
      }

      disconnectedCallback() {
        D.removeEventListener('rv:flavor-change', this.onFlavor);
        if (this.ro) this.ro.disconnect();
      }

      /* The sticker on desktop never covers a word (Cris, 2026-10-03). The headline's size follows the screen's width
         and height, so the free space beside it moves. This measures the headline's letters (their ink, from canvas
         metrics, so the room under a line without descenders counts as free), the other lines of text, the button
         and the chips, then moves the sticker from its preferred spot (--rv-seal-x/y in the CSS) to the nearest spot
         at least 18px from all of them. Moving down costs more than moving sideways, so it stays on the jar's lid. */
      fitSeal() {
        const s = this.seal;
        if (!s) return;
        const set = (x, y) => {
          /* the first fit (page load, fonts) places it without the glide; flavor switches glide */
          const first = !this.fitted;
          this.fitted = true;
          if (first) s.style.transition = 'none';
          if (x == null) {
            s.style.removeProperty('--rv-seal-fx');
            s.style.removeProperty('--rv-seal-fy');
          } else {
            s.style.setProperty('--rv-seal-fx', x + 'px');
            s.style.setProperty('--rv-seal-fy', y + 'px');
          }
          if (first) {
            void s.offsetWidth;
            s.style.removeProperty('transition');
          }
        };
        if (!this.desk.matches || !s.offsetParent || !s.offsetWidth) return set();
        const cs = getComputedStyle(s);
        const px = parseFloat(cs.getPropertyValue('--rv-seal-x')) || 0;
        const py = parseFloat(cs.getPropertyValue('--rv-seal-y')) || 0;
        /* the sticker's resting center, from layout offsets, so the jar's landing animation doesn't move it */
        let cx = s.offsetWidth / 2;
        let cy = s.offsetHeight / 2;
        let el = s;
        while (el && el !== this) {
          cx += el.offsetLeft;
          cy += el.offsetTop;
          el = el.offsetParent;
        }
        if (el !== this) return set();
        const hr = this.getBoundingClientRect();
        cx += hr.left + this.clientLeft;
        cy += hr.top + this.clientTop;
        const r = ((parseFloat(cs.scale) || 1) * s.offsetWidth * 0.97) / 2;
        const boxes = [];
        const range = D.createRange();
        const texts = (root, each) => {
          const w = D.createTreeWalker(root, NodeFilter.SHOW_TEXT);
          let n;
          while ((n = w.nextNode())) if (n.data.trim()) each(n);
        };
        const h1 = this.querySelector('.rv-home-hero__title');
        if (h1) {
          const hc = getComputedStyle(h1);
          const ctx = D.createElement('canvas').getContext('2d');
          if (ctx) ctx.font = `${hc.fontWeight} ${hc.fontSize} ${hc.fontFamily}`;
          texts(h1, (n) => {
            for (let i = 0; i < n.length; i++) {
              if (!n.data[i].trim()) continue;
              range.setStart(n, i);
              range.setEnd(n, i + 1);
              const b = range.getBoundingClientRect();
              const m = ctx && ctx.measureText(n.data[i]);
              const a = m && m.fontBoundingBoxAscent;
              if (!a) boxes.push(b);
              else boxes.push({ left: b.left, right: b.right, top: b.top + a - m.actualBoundingBoxAscent, bottom: b.top + a + m.actualBoundingBoxDescent });
            }
          });
        }
        this.querySelectorAll('.rv-home-hero__text, .rv-home-hero__cta, .rv-home-hero__badges, .rv-home-hero__chips').forEach((root) =>
          texts(root, (n) => {
            if (h1 && h1.contains(n)) return;
            range.selectNodeContents(n);
            boxes.push(...range.getClientRects());
          })
        );
        this.querySelectorAll('.rv-home-hero__btn, .rv-home-hero__chip .rv-chip__label').forEach((el) => boxes.push(el.getBoundingClientRect()));
        const clear = (x, y) =>
          boxes.every((b) => Math.hypot(Math.max(b.left - x, 0, x - b.right), Math.max(b.top - y, 0, y - b.bottom)) - r >= 18);
        /* (the offsets are measured from the sticker's layout box, so the current shift doesn't matter) */
        if (clear(cx + px, cy + py)) return set();
        const spots = [];
        for (let dx = -240; dx <= 60; dx += 4) for (let dy = -90; dy <= 60; dy += 4) spots.push([dx, dy, Math.abs(dx - px) + 2.5 * Math.abs(dy - py)]);
        spots.sort((a, b) => a[2] - b[2]);
        const best = spots.find(([dx, dy]) => clear(cx + dx, cy + dy));
        if (best) set(best[0], best[1]);
        else set();
      }

      hydrate(j) {
        const t = j.querySelector('template');
        if (t) {
          j.appendChild(t.content.cloneNode(true));
          t.remove();
        }
      }

      /* Swap photo layers. The new one fades in sharp over the old one, which blurs out underneath and only hides
         once the fade is over (rv-home-hero.css), so the color field never shows through. If the new photo is still
         loading (a tap right after the page opens), the swap waits for it, at most 1.5s. */
      showPhoto(key) {
        const next = this.photos.find((p) => p.dataset.photo === key);
        if (!next) return;
        this.hydrate(next);
        const token = (this.photoToken = (this.photoToken || 0) + 1);
        const swap = () => {
          if (token !== this.photoToken) return;
          this.photos.forEach((p) => {
            const on = p === next;
            p.classList.toggle('is-active', on);
            if (on) p.removeAttribute('aria-hidden');
            else p.setAttribute('aria-hidden', 'true');
          });
        };
        const img = next.querySelector('img');
        if (!img || img.complete || next.classList.contains('is-active')) return swap();
        img.loading = 'eager'; /* restores on load wait too, so the box never shows empty */
        const t = setTimeout(swap, 1500);
        img.addEventListener('load', () => (clearTimeout(t), swap()), { once: true });
        img.addEventListener('error', () => (clearTimeout(t), swap()), { once: true });
      }

      fill(h) {
        return this.fills.find((f) => f.dataset.handle === h);
      }

      pick(c) {
        /* the Duo sends the first flavor's colors, as the buy box does */
        const f = this.fill(c.value === 'duo' ? this.dataset.first : c.value);
        const v = (k) => (f ? f.style.getPropertyValue('--rv-flavor-' + k).trim() : '');
        this.own = true; /* a chip tap is always the visitor's pick, even before DOMContentLoaded */
        try {
          D.dispatchEvent(
            new CustomEvent('rv:flavor-change', {
              detail: { handle: c.value, name: c.dataset.name, accent: v('accent'), deep: v('deep'), tint: v('tint') },
            })
          );
        } finally {
          this.own = false;
        }
      }

      show(d, quiet) {
        if (!d || !d.handle) return;
        const duo = d.handle === 'duo';
        /* the Duo gets its own half-and-half field and both jars when the hero has them (else the first flavor's) */
        const h = duo ? this.dataset.first : d.handle;
        const f = (duo && this.fill('duo')) || this.fill(h);
        if (!f) return;
        const jarKey = duo && this.jars.some((j) => j.dataset.handle === 'duo') ? 'duo' : h;
        const fillChange = !f.classList.contains('is-active');
        const jarChange = !this.jars.some((j) => j.dataset.handle === jarKey && j.classList.contains('is-active'));

        if (fillChange || jarChange) {
          /* quiet = restored on load: swap instantly (a running load pour keeps going) */
          if (quiet) this.classList.add('is-instant');
          else this.classList.remove('is-pour');
          if (fillChange) {
            const old = this.fills.find((x) => x.classList.contains('is-active'));
            this.fills.forEach((x) => x.classList.remove('is-prev'));
            if (old) old.classList.replace('is-active', 'is-prev');
            void f.offsetWidth; /* start from scale(0) so it spreads out from the middle over the old layer */
            f.classList.add('is-active');
          }
          if (jarChange) {
            this.jars.forEach((j) => {
              const on = j.dataset.handle === jarKey;
              if (on) this.hydrate(j);
              j.classList.toggle('is-active', on);
              if (on) j.removeAttribute('aria-hidden');
              else j.setAttribute('aria-hidden', 'true');
            });
          }
          /* a flavor with its own photo shows it; the Duo and the others show the main photo */
          if (this.photos.length) this.showPhoto(this.photos.some((p) => p.dataset.photo === d.handle) ? d.handle : 'main');
          if (quiet) {
            void this.offsetWidth;
            this.classList.remove('is-instant');
          }
          /* each look has its own sticker spot */
          if (this.refit) this.refit();
        }

        /* chips mirror the visitor's pick (the Duo checks the Duo chip when there is one). Restores leave them alone,
           except when the page opened on the Duo: then a chip is always checked, so it must match what's shown. */
        if (!quiet || this.startDuo) this.chips.forEach((c) => (c.checked = duo ? c.value === 'duo' : c.value === h));

        if (this.touched && this.label) {
          const c = this.chips.find((x) => x.value === h);
          const name = (c && c.dataset.name) || d.name || '';
          const text = duo ? this.dataset.ctaDuo : (this.dataset.ctaFlavor || '').replace('[flavor]', name);
          if (text) this.label.textContent = text;
        }
      }
    }
    customElements.define('rv-home-hero', RvHomeHero);
  }

  /* "Pick your flavor" links: the browser jumps to #buy, then focus moves to the selected (or first) tile.
     A target that is focusable itself (a tabindex attribute: the shop page's #pick around its cards) takes the focus
     instead; inside, controls with the hidden attribute are skipped (the shop cards' always-checked option radios). */
  if (!window.rvToBuyBound) {
    window.rvToBuyBound = true;
    D.addEventListener('click', (e) => {
      const a = e.target.closest && e.target.closest('a[data-rv-to-buy]');
      if (!a || !a.hash || a.pathname !== location.pathname) return;
      const t = D.getElementById(decodeURIComponent(a.hash.slice(1)));
      if (!t) return;
      setTimeout(() => {
        const el = t.hasAttribute('tabindex')
          ? t
          : t.querySelector('input[type="radio"]:checked:not([disabled]):not([hidden])') ||
            t.querySelector(
              'input[type="radio"]:not([disabled]):not([hidden]), button:not([disabled]):not([hidden]), a[href]:not([hidden]), select:not([hidden])'
            );
        if (el) el.focus({ preventScroll: true });
      }, 60);
    });
  }
})();
